import { hasAttachmentVariant, isPhotoAttachment } from './attachmentVariants';
import { normalizeId } from './ids';
import { PUBLIC_DOWNLOAD_VALUES, safeThumb } from './publicFiles';
import { scanOf } from './fileScan';

/**
 * A `file` element's value, read (docs/plans/data-types-file-element.md §2,
 * §6; the 10b contract).
 *
 * The stored value is one attachment id, or — under `allowMultiple` — a list
 * of at most 20, in the order the person gave (the first is the one a tile
 * shows). Empty is absent, '' or []. The item read carries the facts a
 * surface needs to draw each file, keyed by id:
 *
 *   item.files[id] = { name, ext, mime, size, image: { width, height } | null, variants, scan }
 *
 * — only when the reader passes the collection's attachments gate. A value
 * whose id has no facts entry (the gate is off, or the file is gone) shows
 * NOTHING: never a broken image, never an id.
 *
 * A file is not a record: nothing here or in components/common/FileValue.js
 * renders a record badge.
 */

export const FILE_TYPE = 'file';
/** The most files one element holds (the API's `too_many_files { limit }`). */
export const FILE_LIST_LIMIT = 20;
/** The platform's cap when an element sets no `maxSizeMb`. */
export const FILE_PLATFORM_MAX_MB = 100;

export const isFileElement = element => element?.dataType === FILE_TYPE;

/** The ids a value holds, in order, as strings ('' and junk dropped). */
export const fileIdsOf = (value) => {
	const list = Array.isArray(value) ? value : [value];
	return list.map(entry => normalizeId(entry)).filter(id => typeof id === 'string' && id);
};

/**
 * Every attachment id the entries of `list` hold in a file column of the
 * repeating `group` (KPR-934; keepr-api KPR-750), in list order. What a save
 * claims, and what a draft kept on leaving keeps: the form's held uploads
 * (hooks/useHeldFiles) are settled against it, so an upload into an entry is
 * never deleted under a save or a draft that holds it. A heading (an entry with
 * a `section`) holds none. (Here and not in utils/repeatingGroupEntries.js,
 * which many surfaces import: this module reaches the network through the
 * variant cache, and the loading gate counts that against every importer.)
 */
export const groupFileIds = (group, list) => {
	const columns = Array.isArray(group?.options?.columns) ? group.options.columns.filter(isFileElement) : [];
	if (!columns.length || !Array.isArray(list)) return [];
	return list.flatMap(entry => (!entry || typeof entry !== 'object' || typeof entry.section === 'string'
		? []
		: columns.flatMap(column => fileIdsOf(entry.elements?.[column.name]))));
};

export const isEmptyFileValue = value => fileIdsOf(value).length === 0;

/**
 * One file, in the shape 10a's attachment surfaces read (`_id`,
 * `originalName`, `ext`, `mime`, `size`, `image`, `variants`) so the viewer
 * and the variant cache take it unchanged — or null with no facts entry.
 */
export const fileFactsFor = (item, id) => {
	const facts = item?.files && typeof item.files === 'object' ? item.files[id] : null;
	if (!facts || typeof facts !== 'object') return null;
	return {
		_id: id,
		originalName: typeof facts.name === 'string' ? facts.name : '',
		ext: typeof facts.ext === 'string' ? facts.ext : '',
		mime: typeof facts.mime === 'string' ? facts.mime : '',
		size: Number.isFinite(facts.size) ? facts.size : null,
		image: facts.image && typeof facts.image === 'object' ? facts.image : null,
		variants: Array.isArray(facts.variants) ? facts.variants : [],
		// A PUBLIC page's facts (PR 10c) say whether a visitor may download
		// the file and carry its `sm` inline; a signed-in read has neither.
		...publicFacts(facts),
		// A manager's read may count the file's public downloads per link.
		...(Array.isArray(facts.publicDownloads) ? { publicDownloads: facts.publicDownloads } : {}),
		// The malware verdict a signed-in read carries (PR 10e, utils/fileScan).
		...(scanOf(facts) ? { scan: scanOf(facts) } : {}),
	};
};

function publicFacts(facts) {
	// 10e adds `checking` (a verdict on its way) and `unavailable` (flagged).
	if (!PUBLIC_DOWNLOAD_VALUES.includes(facts.download)) return {};
	return { isPublic: true, download: facts.download, thumb: safeThumb(facts.thumb) };
}

/** The files a value holds that the item read describes, in order. */
export const filesOf = (item, value) => fileIdsOf(value)
	.map(id => fileFactsFor(item, id))
	.filter(Boolean);

/**
 * A photo the server sized, with this size made: it shows as a picture.
 * Anything else — a document, a PDF (never a photo: G11), a photo from before
 * thumbnails — shows as the file chip.
 */
export const showsAsPicture = (file, size) => isPhotoAttachment(file) && hasAttachmentVariant(file, size)
	// A public page draws `sm` only from the payload's inline thumb (no byte
	// request per tile): without one, the chip.
	&& !(file?.isPublic && size === 'sm' && !file.thumb);

/**
 * Does a file element show on this read surface at all? The item read's
 * `files` facts say the reader passed the gate for this item; without them,
 * the collection's own switch decides. With attachments off the element is
 * hidden (its values stay stored, and come back with the switch); a surface
 * that knows no collection (a public page — whose reads carry facts only
 * when the link includes files and the gate is on, PR 10c) shows it only
 * when the server sent facts.
 */
export const fileElementVisible = ({ item = null, collection = null } = {}) => (
	Boolean(item?.files && typeof item.files === 'object')
	|| collection?.allowAttachments === true
);

/** "Photos only" — the element takes only what the server's check calls a photo. */
export const isPhotoOnlyElement = element => isFileElement(element) && element?.options?.accept === 'image';

// Owner ruling G8 (the 10b contract): a file, item lookup, user or location
// element holds values that mean nothing as another type, so changing one of
// these OUT of its type is refused while any live item holds a value; and no
// conversion turns any value INTO a file, so a change into `file` is refused
// too (409 type_change_needs_empty { element, count }). Changes INTO a user
// or a location keep today's preview and conversions. A change INTO an item
// lookup is refused too while items hold values, but with an answer — clear
// them (KPR-1136; utils/shapeChange.js madeLookup, the shape dialog). The
// author adds a new element instead. The API owns the rule and the count; the
// card editor says it before the save.
// A `confirmations` count (KPR-390) joins BOTH sides, as in keepr-api
// utils/shapeChange.js: a number typed under the element is nobody's
// confirmation, and a count made a number would become a value writers type
// while the ledger of who confirmed lingers unread. An element nobody has
// confirmed changes freely either way.
// A `repeating-group` (KPR-679) joins both sides too: a list of entries means
// nothing as another type, and nothing turns a value into a list of entries.
export const TYPE_CHANGE_NEEDS_EMPTY_TYPES = ['file', 'card-lookup', 'user', 'location', 'confirmations', 'repeating-group'];
const NEEDS_EMPTY_INTO = ['file', 'confirmations', 'repeating-group'];

export const typeChangeNeedsEmpty = (fromType, toType) => Boolean(fromType && toType && fromType !== toType)
	&& (TYPE_CHANGE_NEEDS_EMPTY_TYPES.includes(fromType) || NEEDS_EMPTY_INTO.includes(toType));
